import React, { useEffect } from "react";
import { Box, Typography, useTheme, makeStyles, CircularProgress } from "@material-ui/core";
import CompositionBox from "../../components/CompositionBox";
import Title from "../../components/Title";
import { ApplicationState } from "../../store";
import { connect } from "react-redux";
import { Info } from "../../components/Info";
import { SecondaryButton } from "../../components/Buttons";
import { Promotion } from "../../store/ComponentTexts";
import { PromotionBlock } from "../../components/PromotionBlock";
import clsx from "clsx";
import { Theme } from "../../.themes";
import { Helmet } from "react-helmet";
import Header from "../../components/Header";

interface ConfirmationProps {
  texts?: {
    confirmationHeader?: string;
    confirmationBody?: string;
    confirmationExtraInfo?: string;
    confirmationExtraUrl1?: {
      urlText?: string;
      link?: string;
    };
    confirmationExtraUrl2?: {
      urlText?: string;
      link?: string;
    };
    confirmationPromotion1?: Promotion;
  };
  inputs?: { redirectLink: string; confirmationPageUrl?: string; partner?: string };
  profile?: { cxScriptEnabled?: boolean };
  decoupledRendering?: {
    mainLogo: string;
    mainLogoAlt: string;
    secondaryLogo: string;
    secondaryLogoAlt: string;
    isDecoupledRendering: boolean;
  };
}

//for desktop:
const useStyles = makeStyles((theme: Theme) => ({
  contentBody: {
    [theme.breakpoints.up("sm")]: {
      maxWidth: "50%",
    },
  },
  topMargin: {
    [theme.breakpoints.up("sm")]: {
      marginTop: "2rem",
    },
    marginTop: "1rem",
    ...theme?.ConfirmationPage?.TopMargin,
  },
  topMarginPromo: {
    marginTop: "4rem",
  },
  info: {
    border: "none",
    backgroundColor: theme.palette.background.default,
  },
  centeredText: {
    textAlign: "center",
  },
  header: {
    backgroundColor: theme.palette.backgroundSecondary?.default,
    textAlign: "center",
    padding: `${theme.spacing(6)}px 0`,
    marginBottom: "calc(-2rem - 1px)",
    "& hr": {
      border: "none",
      width: "75px",
      backgroundColor: "transparent",
      margin: 0,
    },
    ...theme.header,
  },
  fullWidthWrapper: {
    width: "100%",
    margin: "0 auto",
    padding: 0,
    marginBottom: `${theme.spacing(6)}px`,
    maxWidth: "100%",
  },
  outlinedButton: {
    "& a": {
      color: theme.palette.primary.contrastText,
      textDecoration: "none",
      ...theme.ConfirmationPage?.ConfirmationButtonOutlined,
    },
    ...theme.outlinedButton,
  },
  containedButton: {
    "& a": {
      color: theme.palette.primary.contrastText,
      textDecoration: "none",
      ...theme.ConfirmationPage?.ConfirmationButtonContained,
    },
  },
  layout: {
    backgroundColor: theme?.appBackgroundColor ?? "#fff",
  },
}));

const Done: React.FC<ConfirmationProps> = (props: ConfirmationProps) => {
  const theme: Theme = useTheme();
  const [showDonePage, setShowDonePage] = React.useState(false);
  const classes = useStyles(theme);

  if (props.texts) {
    props.texts.confirmationBody = props.texts?.confirmationBody ?? "Du får strax ett bekräftelsemejl skickat till din mejl.";
    props.texts.confirmationHeader = props.texts?.confirmationHeader ?? "Tack för ditt köp";
    if (props.texts.confirmationExtraUrl1) {
      props.texts.confirmationExtraUrl1.urlText = props.texts.confirmationExtraUrl1?.urlText ?? "Tillbaka till startsidan";
    }
  }

  localStorage.setItem("Webshop Page", "Done");

  useEffect(() => {
    const handleRedirectMessage = (event: MessageEvent) => {
      if (event.data.type === "redirect-to-webshop-confirmation-failed") {
        setShowDonePage(true);
      } else if (event.data.type === "redirect-to-webshop-confirmation-success") {
        //redirect-to-webshop-confirmation-success;
      }
    };

    if (props.inputs?.confirmationPageUrl) {
      if (!props.decoupledRendering?.isDecoupledRendering && window.top !== window.self && window.top) {
        // when in iframe, post to parent
        window.top.postMessage({ type: "redirect-to-webshop-confirmation" }, window.top.location.origin);
        window.addEventListener("message", handleRedirectMessage);
      } else {
        window.location.href = props.inputs.confirmationPageUrl;
      }
    } else {
      setShowDonePage(true);
    }
    return () => {
      window.removeEventListener("message", handleRedirectMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!showDonePage) {
    return (
      <Box sx={{ display: "flex" }}>
        <CircularProgress />
      </Box>
    );
  } else {
    return (
      <div className={classes.layout}>
        {props.decoupledRendering?.isDecoupledRendering ? <Header /> : null}
        {props.profile?.cxScriptEnabled && (
          <Helmet>
            <script
              id='cf-program-ODj5lt'
              src='https://digitalfeedback.euro.confirmit.com/api/digitalfeedback/loader?programKey=ODj5lt'
              async
            ></script>
          </Helmet>
        )}
        <Box
          component='div'
          display='flex'
          flexDirection='column'
          className={clsx(classes.fullWidthWrapper, "centered", "confirmation-box")}
        >
          <div className={classes.header}>
            <CompositionBox maxWidth={theme.containerMaxWidth ?? 412} className={classes.centeredText}>
              <Box className={classes.topMargin}>
                <Title title={props.texts?.confirmationHeader} className={classes.centeredText} />
                <hr />
                {props.texts?.confirmationBody && (
                  <Typography variant='body1' component='p'>
                    {props.texts?.confirmationBody}
                  </Typography>
                )}
                {props.texts?.confirmationExtraInfo && props.inputs?.redirectLink && (
                  <div className={[classes.topMargin].join(" ")}>
                    <Info title={props.texts?.confirmationExtraInfo} />
                  </div>
                )}
              </Box>
              <div className={classes.topMargin}>
                <SecondaryButton fullWidth={true} href={props.texts?.confirmationExtraUrl1?.link} className={classes.outlinedButton}>
                  {props.texts?.confirmationExtraUrl1?.urlText}
                </SecondaryButton>
              </div>
            </CompositionBox>
          </div>

          <CompositionBox maxWidth={theme.containerMaxWidth ?? 412} className={clsx(classes.centeredText, classes.containedButton)}>
            {props.texts?.confirmationPromotion1 && (
              <PromotionBlock {...props.texts.confirmationPromotion1} className={classes.topMarginPromo} />
            )}
          </CompositionBox>
        </Box>
      </div>
    );
  }
};

// export default Done;
export default connect((state: ApplicationState) => state, {})(Done);
